type PortalPublishConfigRequest = {
  config: unknown
}

type PortalPublishConfigResponse = {
  version: number
  etag: string
}

type PortalGetConfigResponse = {
  version: number
  config: unknown
}

type PortalValidateConfigResponse = {
  valid: boolean
  errors: Array<{ instancePath: string; message: string }>
}

type PortalListVersionsResponse = {
  versions: Array<{ version: number; createdAt: any; configHash: string }>
}

type PortalRollbackResponse = {
  version: number
  etag: string
}

/**
 * Đường gốc của API. Màn nào tự `fetch('/portal/admin/v1/...')` thì chỉ chạy
 * nhờ rewrite của Firebase Hosting — hỏng ngay khi chạy `vite dev` hoặc khi
 * VITE_PORTAL_API_BASE trỏ đi nơi khác.
 */
export function portalApiUrl(path: string) {
  return joinUrl(baseUrl(), path);
}

function baseUrl() {
  const v = (import.meta as any).env?.VITE_PORTAL_API_BASE
  return typeof v === 'string' ? v.replace(/\/$/, '') : ''
}

function joinUrl(base: string, path: string) {
  if (!base) return path
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`
}

async function jsonFetch<T>(
  url: string,
  opts: RequestInit & { headers?: Record<string, string> } = {},
): Promise<T> {
  const res = await fetch(url, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(opts.headers ?? {}),
    },
  })
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`
    try {
      const j = await res.json()
      const code = j?.error?.code
      if (typeof code === 'string') msg = code
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}

async function jsonFetchWithMeta<T>(
  url: string,
  opts: RequestInit & { headers?: Record<string, string> } = {},
): Promise<{ status: number; headers: Headers; body: T | null }> {
  const res = await fetch(url, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(opts.headers ?? {}),
    },
  })
  if (res.status === 304) return { status: 304, headers: res.headers, body: null }
  const text = await res.text()
  const body = text ? (JSON.parse(text) as T) : null
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`
    try {
      const j: any = body
      const code = j?.error?.code
      if (typeof code === 'string') msg = code
    } catch {
      // ignore
    }
    throw new Error(msg)
  }
  return { status: res.status, headers: res.headers, body }
}



export async function portalPublishConfig(args: {
  tenantId: string
  appId: string
  env: string
  idToken: string
  config: unknown
}): Promise<PortalPublishConfigResponse> {
  const { tenantId, appId, env, idToken, config } = args
  return jsonFetch(
    joinUrl(
      baseUrl(),
      `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/envs/${env}/config:publish`,
    ),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ config } satisfies PortalPublishConfigRequest),
    },
  )
}

export async function portalGetConfig(args: {
  tenantId: string
  appId: string
  env: string
  idToken: string
}): Promise<PortalGetConfigResponse> {
  const { tenantId, appId, env, idToken } = args
  return jsonFetch(
    joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/envs/${env}/config`),
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    },
  )
}

export async function portalGetConfigRaw(args: {
  tenantId: string
  appId: string
  env: string
  idToken: string
  ifNoneMatch?: string
}): Promise<{ status: number; etag: string | null; body: PortalGetConfigResponse | null }> {
  const { tenantId, appId, env, idToken, ifNoneMatch } = args
  const resp = await jsonFetchWithMeta<PortalGetConfigResponse>(
    joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/envs/${env}/config`),
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(ifNoneMatch ? { 'if-none-match': ifNoneMatch } : {}),
      },
    },
  )
  return { status: resp.status, etag: resp.headers.get('etag'), body: resp.body }
}

export async function portalValidateConfig(args: {
  tenantId: string
  appId: string
  env: string
  idToken: string
  config: unknown
}): Promise<PortalValidateConfigResponse> {
  const { tenantId, appId, env, idToken, config } = args
  return jsonFetch(
    joinUrl(
      baseUrl(),
      `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/envs/${env}/config:validate`,
    ),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ config }),
    },
  )
}

export async function portalListConfigVersions(args: {
  tenantId: string
  appId: string
  env: string
  idToken: string
  limit?: number
}): Promise<PortalListVersionsResponse> {
  const { tenantId, appId, env, idToken, limit } = args
  const q = typeof limit === 'number' ? `?limit=${encodeURIComponent(String(limit))}` : ''
  return jsonFetch(
    joinUrl(
      baseUrl(),
      `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/envs/${env}/config:versions${q}`,
    ),
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    },
  )
}

export async function portalGetSubscriptionMetrics(args: {
  tenantId: string
  appId: string
  env: string
  idToken: string
  period?: 'day' | 'month'
}): Promise<any> {
  const { tenantId, appId, env, idToken, period } = args
  const q = period ? `?period=${period}` : ''
  return jsonFetch(
    joinUrl(
      baseUrl(),
      `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/envs/${env}/metrics/subscriptions${q}`,
    ),
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    },
  )
}

export async function portalGetTenantMetrics(args: {
  tenantId: string
  idToken: string
}): Promise<any> {
  const { tenantId, idToken } = args
  return jsonFetch(
    joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/metrics`),
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    },
  )
}

export async function portalGetTenantOverview(args: {
  tenantId: string
  idToken: string
  period?: 'month' | 'day'
}): Promise<any> {
  const { tenantId, idToken, period = 'month' } = args
  return jsonFetch(
    joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/metrics/overview?period=${period}`),
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    },
  )
}

export async function portalGetTenantSettings(args: { tenantId: string; idToken: string }) {
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${args.tenantId}/settings`), {
    method: 'GET',
    headers: { Authorization: `Bearer ${args.idToken}` },
  })
}

export async function portalSaveTenantSettings(args: { tenantId: string; idToken: string; settings: any }) {
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${args.tenantId}/settings`), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${args.idToken}` },
    body: JSON.stringify({ settings: args.settings }),
  })
}

export async function portalListPat(args: { tenantId: string; idToken: string }) {
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${args.tenantId}/api-keys`), {
    method: 'GET',
    headers: { Authorization: `Bearer ${args.idToken}` },
  })
}

export async function portalCreatePat(args: { tenantId: string; idToken: string; name: string }) {
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${args.tenantId}/api-keys`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${args.idToken}` },
    body: JSON.stringify({ name: args.name }),
  })
}

export async function portalRevokePat(args: {
  tenantId: string
  keyId: string
  idToken: string
}): Promise<{ ok: boolean }> {
  return jsonFetch(
    joinUrl(baseUrl(), `/portal/admin/v1/tenants/${args.tenantId}/api-keys/${args.keyId}`),
    { method: 'DELETE', headers: { Authorization: `Bearer ${args.idToken}` } },
  )
}

export async function portalRollbackConfig(args: {
  tenantId: string
  appId: string
  env: string
  idToken: string
  version: number
}): Promise<PortalRollbackResponse> {
  const { tenantId, appId, env, idToken, version } = args
  return jsonFetch(
    joinUrl(
      baseUrl(),
      `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/envs/${env}/config:rollback`,
    ),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ version }),
    },
  )
}

export async function saasCreateApp(args: {
  name: string
  bundleId?: string
  packageName?: string
  idToken: string
}): Promise<{ ok: boolean; app: any; apiKey: string }> {
  const { name, bundleId, packageName, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/apps`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ name, bundleId, packageName }),
  })
}

export async function saasDeleteApp(args: {
  appId: string
  idToken: string
}): Promise<{ ok: boolean }> {
  const { appId, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/apps/${appId}`), {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })
}

export async function saasCreateDeveloperKey(args: {
  name: string
  idToken: string
}): Promise<{ ok: boolean; apiKey: string }> {
  const { name, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/developer-keys`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ name }),
  })
}

export async function saasGetAppWebhook(args: {
  tenantId: string
  appId: string
  idToken: string
}): Promise<{ url: string; secretMasked?: string }> {
  const { tenantId, appId, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/webhook`), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  })
}

export async function saasSaveAppWebhook(args: {
  tenantId: string
  appId: string
  url: string
  secret?: string
  idToken: string
}): Promise<{ ok: boolean }> {
  const { tenantId, appId, url, secret, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/webhook`), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(secret ? { url, secret } : { url }),
  })
}

export async function saasGetAppStoreKit(args: {
  tenantId: string
  appId: string
  idToken: string
}): Promise<{ bundleId: string; issuerId: string; keyId: string; hasP8Content?: boolean }> {
  const { tenantId, appId, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/apple-storekit`), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  })
}

export async function saasSaveAppStoreKit(args: {
  tenantId: string
  appId: string
  bundleId: string
  issuerId: string
  keyId: string
  p8Content?: string
  idToken: string
}): Promise<{ ok: boolean }> {
  const { tenantId, appId, bundleId, issuerId, keyId, p8Content, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/apple-storekit`), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(p8Content ? { bundleId, issuerId, keyId, p8Content } : { bundleId, issuerId, keyId }),
  })
}

export async function saasGetAppGooglePlay(args: {
  tenantId: string
  appId: string
  idToken: string
}): Promise<{ hasServiceAccountJson?: boolean }> {
  const { tenantId, appId, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/google-play`), {
    method: 'GET',
    headers: { Authorization: `Bearer ${idToken}` },
  })
}

export async function saasSaveAppGooglePlay(args: {
  tenantId: string
  appId: string
  serviceAccountJson?: string
  idToken: string
}): Promise<{ ok: boolean }> {
  const { tenantId, appId, serviceAccountJson, idToken } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/google-play`), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(serviceAccountJson ? { serviceAccountJson } : {}),
  })
}

export async function saasUpdateAppConfig(args: {
  idToken: string
  appId: string
  name?: string
  bundleId?: string
  packageName?: string
}) {
  const { idToken, appId, name, bundleId, packageName } = args
  return jsonFetch(joinUrl(baseUrl(), `/portal/admin/v1/apps/${appId}`), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ name, bundleId, packageName }),
  })
}

export async function portalListCustomers(args: {
  tenantId: string
  appId: string
  idToken: string
  limit?: number
  startAfter?: string
}): Promise<{ customers: any[]; nextCursor: string | null }> {
  const { tenantId, appId, idToken, limit, startAfter } = args
  const params = new URLSearchParams()
  if (limit !== undefined) params.set('limit', String(limit))
  if (startAfter) params.set('startAfter', startAfter)
  const qs = params.toString()
  return jsonFetch(
    joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/customers${qs ? `?${qs}` : ''}`),
    { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } },
  )
}

export async function portalGetCustomer(args: {
  tenantId: string
  appId: string
  appUserId: string
  idToken: string
}): Promise<{ customer: any; events: any[] }> {
  const { tenantId, appId, appUserId, idToken } = args
  return jsonFetch(
    joinUrl(baseUrl(), `/portal/admin/v1/tenants/${tenantId}/apps/${appId}/customers/${encodeURIComponent(appUserId)}`),
    { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } },
  )
}
